import {observer} from 'mobx-react-lite';

import {ScrollAnchorWrapper} from 'UI/components/layout/ScrollAnchorWrapper';

import {useBlockRenderer} from './useBlockRenderer';

import type {BlockUnionType} from './useBlockRenderer';

/**
 * The `ComponentProps` interface defines the shape of the properties object that is expected for this component.
 * It outlines the required properties that needs to be provided when utilizing this component expecting an object of this type.
 */
interface ComponentProps {
    /**
     * The `content` property contains an array of block data used for rendering dynamic block components.
     * Each item in the array is expected to be either a `HeroBlockType` or `TimelineBlockType` object.
     * If no content is provided, it can be `null` or `undefined`, and the component will render nothing.
     */
    content: (BlockUnionType)[] | null | undefined;
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'BlockRenderer'
     */
    testId?: string;
}

/**
 * A React component that dynamically renders an array of blocks using their associated components and data.
 * This component receives structured block content (usually from a CMS like Payload) and delegates the rendering
 * of each block to its corresponding React component. It uses the `useBlockRenderer` hook to resolve the mapping
 * between block data and renderable components. Each block is rendered using its unique `id` as a key, and all
 * associated data is spread as props into the block component.
 *
 * @param props         The component props.
 * @param props.content The array of block entries to be rendered, typically containing a `blockType`, `id`, and other data.
 * @returns A rendered list of block components or `null` if no blocks are available.
 *
 * @example
 * ```tsx
 * <BlockRenderer content={page.blocks} />
 * ```
 */
const BlockRenderer = observer(({content}: ComponentProps) => {
    const {blocks} = useBlockRenderer(content);

    return blocks ? blocks.map((block, index) => {
        const {Component, data} = block;

        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        return Component ? (
            <ScrollAnchorWrapper
                key={data.id}
                isTop={index === 0}
                labelledBy={data.id}
                linkId={data.linkId ? `#${data.linkId}` : undefined}
            >
                {/* eslint-disable-next-line react/jsx-props-no-spreading */}
                <Component {...data as any} />
            </ScrollAnchorWrapper>
        ) : null;
    }) : null;
});

BlockRenderer.displayName = 'BlockRenderer';

export {BlockRenderer};